import { useState, useEffect } from 'react';

/**
 * Hook to dynamically extract media duration from a URL when the stored duration is 0 or missing.
 * Uses the HTML5 Video/Audio API with preload="metadata" to only download file headers.
 */
export function useMediaDuration(
  mediaUrl: string | undefined | null,
  storedDuration: number | undefined | null
): number | null {
  const [duration, setDuration] = useState<number | null>(storedDuration || null);

  useEffect(() => {
    // If we already have a valid stored duration, use it
    if (storedDuration && storedDuration > 0) {
      setDuration(storedDuration);
      return;
    }

    // No URL to extract from
    if (!mediaUrl) return;

    let cancelled = false;

    // Determine if this is likely an audio file
    const lowerUrl = mediaUrl.toLowerCase();
    const isAudio = /\.(mp3|wav|ogg|aac|flac|m4a|wma|opus)(\?|$)/.test(lowerUrl);
    const tag = isAudio ? 'audio' : 'video';

    const tryExtract = (useCors: boolean) => {
      return new Promise<number | null>((resolve) => {
        const media = document.createElement(tag);
        media.preload = 'metadata';
        if (useCors) {
          media.crossOrigin = 'anonymous';
        }

        const timeout = setTimeout(() => {
          media.removeAttribute('src');
          media.load();
          resolve(null);
        }, 10000);

        media.onloadedmetadata = () => {
          clearTimeout(timeout);
          const dur = isFinite(media.duration) && media.duration > 0
            ? Math.round(media.duration)
            : null;
          media.removeAttribute('src');
          media.load();
          resolve(dur);
        };

        media.onerror = () => {
          clearTimeout(timeout);
          media.removeAttribute('src');
          media.load();
          resolve(null);
        };

        media.src = mediaUrl;
      });
    };

    (async () => {
      // Try without CORS first (works for public CDN URLs)
      let result = await tryExtract(false);
      // If that failed, try with CORS
      if (result === null) {
        result = await tryExtract(true);
      }
      // If video element failed for audio, try audio element
      if (result === null && !isAudio) {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        result = await new Promise<number | null>((resolve) => {
          const timeout = setTimeout(() => {
            audio.removeAttribute('src');
            audio.load();
            resolve(null);
          }, 10000);
          audio.onloadedmetadata = () => {
            clearTimeout(timeout);
            const dur = isFinite(audio.duration) && audio.duration > 0
              ? Math.round(audio.duration)
              : null;
            audio.removeAttribute('src');
            audio.load();
            resolve(dur);
          };
          audio.onerror = () => {
            clearTimeout(timeout);
            audio.removeAttribute('src');
            audio.load();
            resolve(null);
          };
          audio.src = mediaUrl;
        });
      }
      if (!cancelled && result !== null) {
        setDuration(result);
      }
    })();

    return () => { cancelled = true; };
  }, [mediaUrl, storedDuration]);

  return duration;
}

export function formatDuration(durationSeconds: number | null | undefined): string {
  if (!durationSeconds || durationSeconds <= 0) return '';
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = (durationSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}
